import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { AllowChangeGrid } from '../../../../../services/modules/product-colors-packs/product-colors-packs.types';
import { BatchPasteGridParams } from '../../../../../services/modules/productDetails/productDetails.types';
import { UpdateItemFormat } from '../../../../orders/products/orderProducts.types';
import { AvailabilityEnum } from '../../../../products/ProductTypes';
import {
  FutureStockSkuDTO,
  ProductSkuBaseDTO,
  ReadyForDeliverySkuDTO,
} from '../../../../products/details/productDetails.types';
import { ProductDetailsDTO } from '../../../../products/products-details-v2/types/productDetails.types';
import { offlineOrdersRepository } from '../../../repositories/offlineOrders.repository';
import { onlineOrderRepositoryOfflineAdapters } from '../../onlineOrder.repository.offlineAdapters';
import { productGridRepositoryOfflineAdapters } from '../../productGrid.repository.offlineAdapters';
import {
  BuildCombinationKeyParams,
  FormatUpdateItemParams,
  GetCombinationGridParams,
  PasteInOrderCombinationsParams,
} from '../../types/productDetailsPasteGrid.utils.types';
import ProductDetailsQueryUtils from './productDetailsQuery.utils';

export default class ProductDetailsPasteGridUtils {
  static async batchPasteGrid(params: BatchPasteGridParams) {
    if (!params.orderIds?.length) {
      throw new Error('orderIds required for batchPasteGrid');
    }

    if (params.pasteInAllCombinations) {
      return this.pasteInAllCombinations(params);
    }
    if (params.unselectedCombinations?.length) {
      return this.pasteWithUnselectedCombinations(params);
    }
    if (params.combinations?.length) {
      return this.pasteWithCombinations(params);
    }

    throw new Error('[batchPasteGrid] invalid parameters');
  }

  private static async pasteInAllCombinations(params: BatchPasteGridParams) {
    const combinations = await this.getCombinationsToPaste(params);
    return this.pasteInCombinations(combinations, params);
  }

  private static async pasteWithUnselectedCombinations(params: BatchPasteGridParams) {
    const combinations = await this.getCombinationsFilteredByUnselected(params);
    return this.pasteInCombinations(combinations, params);
  }

  private static async pasteWithCombinations(params: BatchPasteGridParams) {
    const combinations = await this.getSpecificCombinations(params);
    return this.pasteInCombinations(combinations, params);
  }

  private static async pasteInCombinations(
    combinations: ProductDetailsDTO[],
    params: BatchPasteGridParams,
  ) {
    const grouped = this.groupCombinationsByOrderId(combinations);

    await Promise.all(
      Object.entries(grouped).map(([orderId, orderCombinations]) =>
        this.pasteInOrderCombinations({
          params,
          orderId,
          orderCombinations: orderCombinations ?? [],
        }),
      ),
    );
  }

  private static async getCombinationsToPaste(params: BatchPasteGridParams) {
    const combinations = await ProductDetailsQueryUtils.getCombinations(params);
    return combinations.filter(this.canPasteInCombination);
  }

  private static canPasteInCombination(combination: ProductDetailsDTO) {
    switch (combination.availability) {
      case AvailabilityEnum.READY_FOR_DELIVERY:
        return combination.allowChangeImmediateGrid === AllowChangeGrid.ALLOW;
      case AvailabilityEnum.INFINITE_PRE_ORDER:
      case AvailabilityEnum.MATERIAL_PRE_ORDER:
      case AvailabilityEnum.FUTURE_STOCK:
        return combination.allowChangePreOrderGrid === AllowChangeGrid.ALLOW;
      default:
        return false;
    }
  }

  private static groupCombinationsByOrderId(combinations: ProductDetailsDTO[]) {
    return R.groupBy((combination) => combination.orderId || '', combinations);
  }

  private static async pasteInOrderCombinations({
    params,
    orderId,
    orderCombinations,
  }: PasteInOrderCombinationsParams) {
    const priceTableId = await this.getOrderPriceTableId(orderId);
    const copiedGridQuantityMap = this.getCopiedGridQuantityMap(params);

    const items: UpdateItemFormat[] = [];

    for (const combination of orderCombinations) {
      const grid = await this.getCombinationGrid({ orderId, priceTableId, combination });
      grid.skus.forEach((gridItem) => {
        const quantity = copiedGridQuantityMap.get(gridItem.size.id) ?? 0;
        items.push(this.formatUpdateItem({ quantity, gridItem, combination }));
      });
    }

    await onlineOrderRepositoryOfflineAdapters(orderId).createOrUpdateOrderList(items);
  }

  private static async getOrderPriceTableId(orderId: string) {
    const order = await offlineOrdersRepository.getByIdOrThrow(orderId);
    return order.priceTable.id;
  }

  private static getCopiedGridQuantityMap(params: BatchPasteGridParams) {
    return new Map<string, number>(params.copiedGrid.map((item) => [item.sizeId, item.quantity]));
  }

  private static async getCombinationGrid({
    orderId,
    combination,
    priceTableId,
  }: GetCombinationGridParams) {
    const response = await productGridRepositoryOfflineAdapters.findProductGrid({
      orderId,
      priceTableId,
      availability: combination.availability,
      productColorId: combination.productColorId,
      deliveryWindowId: this.getDeliveryWindowFromCombination(combination),
    });

    if (!response.ok || !response.data?.length) {
      throw new Error('[getCombinationGrid] error fetching product grid for combination');
    }

    return response.data[0];
  }

  private static formatUpdateItem({
    quantity,
    gridItem,
    combination,
  }: FormatUpdateItemParams): UpdateItemFormat {
    const canChangeItemQuantity = this.canChangeItemQuantity(gridItem, quantity);

    return {
      skuId: gridItem.skuId,
      id: gridItem.orderItemId ?? uuidv4(),
      availability: combination.availability,
      productColorId: combination.productColorId,
      deliveryWindowId: this.getDeliveryWindowFromCombination(combination),
      quantity: canChangeItemQuantity ? quantity : (gridItem.orderItemQuantity ?? 0),
    };
  }

  private static canChangeItemQuantity(gridItem: ProductSkuBaseDTO, quantity: number) {
    return gridItem.available && this.validateByAvailability(gridItem, quantity);
  }

  private static validateByAvailability(gridItem: ProductSkuBaseDTO, quantity: number) {
    switch (gridItem.availability) {
      case AvailabilityEnum.READY_FOR_DELIVERY:
        const stockItem = gridItem as ReadyForDeliverySkuDTO;
        return stockItem.availableAmount + (stockItem.orderItemQuantity ?? 0) >= quantity;
      case AvailabilityEnum.FUTURE_STOCK:
        const futureStockItem = gridItem as FutureStockSkuDTO;
        return (
          futureStockItem.availableAmount + (futureStockItem.orderItemQuantity ?? 0) >= quantity
        );
      default:
        return true;
    }
  }

  private static async getCombinationsFilteredByUnselected(
    params: BatchPasteGridParams,
  ): Promise<ProductDetailsDTO[]> {
    const allCombinations = await this.getCombinationsToPaste(params);
    const unselectedCombinationsSet = new Set(
      params.unselectedCombinations?.map(this.buildCombinationKey),
    );
    const filteredCombinations = allCombinations.filter(
      (combination) => !unselectedCombinationsSet.has(this.buildCombinationKey(combination)),
    );
    return filteredCombinations;
  }

  private static async getSpecificCombinations(
    params: BatchPasteGridParams,
  ): Promise<ProductDetailsDTO[]> {
    const allCombinations = await this.getCombinationsToPaste(params);
    const paramCombinationsSet = new Set(params.combinations?.map(this.buildCombinationKey));
    return allCombinations.filter((combination) =>
      paramCombinationsSet.has(this.buildCombinationKey(combination)),
    );
  }

  private static buildCombinationKey({
    orderId,
    availability,
    productColorId,
    deliveryWindowId,
    futureStockDeliveryWindowId,
  }: BuildCombinationKeyParams) {
    return `${orderId}-${productColorId}-${availability}-${deliveryWindowId ?? futureStockDeliveryWindowId ?? null}`;
  }

  private static getDeliveryWindowFromCombination(combination: ProductDetailsDTO) {
    return combination.deliveryWindowId ?? combination.futureStockDeliveryWindowId ?? null;
  }
}
